import { h3ToGeo, hexArea } from 'h3-js'

const getDefaultState = () => {
  return {
    minDate: new Date('2018-01-01'),
    maxDate: new Date(),
    hexagonResolution: 2,
    minMaxDuration: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    selectedMinMaxDuration: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    inOdoMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    inOdoSelectedMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    outOdoMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    outOdoSelectedMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    inDurMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    inDurSelectedMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    outDurMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    outDurSelectedMinMax: [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER],
    selectedHexagons: new Set(),
    locationAggregates: null,
    resolutionToRequest: 2,
    selectedWeekdays: [],
    selectedProduct: [],
    selectedEnergy: [],
    selectedMinVehicles: 4,
    graphDataStructure: {
      durationDistribution: [],
      countDistribution: [],
      countDistributionLegend: []
    },
    locationDataWithGeoInfo: [],
    edgeLayer: []
  }
}

const state = getDefaultState()

const getters = {
  getMinDate: (state) => {
    const offset = state.minDate.getTimezoneOffset()
    return (new Date(state.minDate - (offset * 60 * 1000))).toISOString().split('T')[0]
  },
  getMaxDate: (state) => {
    const offset = state.maxDate.getTimezoneOffset()
    return (new Date(state.maxDate - (offset * 60 * 1000))).toISOString().split('T')[0]
  },
  getHexagonResolution: (state) => {
    return state.hexagonResolution
  },
  getResolutionToRequest: (state) => {
    return state.resolutionToRequest
  },
  getMinMaxDuration: state => {
    return state.minMaxDuration
  },
  getSelectedMinMaxDuration: state => {
    return state.selectedMinMaxDuration
  },
  getInOdoMinMax: state => {
    return state.inOdoMinMax
  },
  getInOdoSelectedMinMax: state => {
    return state.inOdoSelectedMinMax
  },
  getOutOdoMinMax: state => {
    return state.outOdoMinMax
  },
  getOutOdoSelectedMinMax: state => {
    return state.outOdoSelectedMinMax
  },
  getInDurMinMax: state => {
    return state.inDurMinMax
  },
  getInDurSelectedMinMax: state => {
    return state.inDurSelectedMinMax
  },
  getOutDurMinMax: state => {
    return state.outDurMinMax
  },
  getOutDurSelectedMinMax: state => {
    return state.outDurSelectedMinMax
  },
  getSelectedHexagons: state => {
    return state.selectedHexagons
  },
  getLocationAggregates: state => {
    return state.locationAggregates
  },
  getSelectedWeekdays: state => {
    return state.selectedWeekdays
  },
  getSelectedProduct: state => {
    return state.selectedProduct
  },
  getSelectedEnergy: state => {
    return state.selectedEnergy
  },
  getGraphDataStructure: state => (plotDataName) => {
    return state.graphDataStructure[plotDataName]
  },
  getLocationDataWithGeoInfo: state => {
    return state.locationDataWithGeoInfo
  },
  getSelectedMinVehicles: state => {
    return state.selectedMinVehicles
  },
  getEdgeLayer: state => {
    return state.edgeLayer
  }
}

const mutations = {
  setEdgeLayer (state, edgeLayer) {
    state.edgeLayer = Object.freeze(edgeLayer)
  },
  setSelectedMinVehicles (state, selectedMinVehicles) {
    state.selectedMinVehicles = selectedMinVehicles
  },
  setSelectedWeekdays (state, selectedWeekdays) {
    state.selectedWeekdays = selectedWeekdays
  },
  setSelectedProduct (state, selectedProduct) {
    state.selectedProduct = selectedProduct
  },
  setSelectedEnergy (state, selectedEnergy) {
    state.selectedEnergy = selectedEnergy
  },
  setMinDate (state, minDate) {
    state.minDate = new Date(minDate)
  },
  setMaxDate (state, maxDate) {
    state.maxDate = new Date(maxDate)
  },
  setHexagonResolution (state, hexagonResolution) {
    state.hexagonResolution = hexagonResolution
  },
  setResolutionToRequest (state, resolutionToRequest) {
    state.resolutionToRequest = resolutionToRequest
  },
  setMinMaxDuration (state, minMaxDurationArray) {
    state.minMaxDuration = minMaxDurationArray
  },
  setInOdoMinMax (state, inOdoMinMaxArray) {
    state.inOdoMinMax = inOdoMinMaxArray
  },
  setOutOdoMinMax (state, outOdoMinMaxArray) {
    state.outOdoMinMax = outOdoMinMaxArray
  },
  setInDurMinMax (state, inDurMinMaxArray) {
    state.inDurMinMax = inDurMinMaxArray
  },
  setOutDurMinMax (state, outDurMinMaxArray) {
    state.outDurMinMax = outDurMinMaxArray
  },
  setSelectedMinMaxDuration (state, minMaxDurationArray) {
    state.selectedMinMaxDuration = minMaxDurationArray
  },
  setInOdoSelectedMinMax (state, inOdoMinMaxArray) {
    state.inOdoSelectedMinMax = inOdoMinMaxArray
  },
  setOutOdoSelectedMinMax (state, outOdoMinMaxArray) {
    state.outOdoSelectedMinMax = outOdoMinMaxArray
  },
  setInDurSelectedMinMax (state, inDurMinMaxArray) {
    state.inDurSelectedMinMax = inDurMinMaxArray
  },
  setOutDurSelectedMinMax (state, outDurMinMaxArray) {
    state.outDurSelectedMinMax = outDurMinMaxArray
  },
  setSelectedMinDuration (state, minValue) {
    if (!isNaN(parseInt(minValue))) {
      state.selectedMinMaxDuration[0] = parseInt(minValue)
    } else {
      state.selectedMinMaxDuration[0] = state.minMaxDuration[0]
    }
  },
  setInOdoSelectedMin (state, minValue) {
    if (!isNaN(parseInt(minValue))) {
      state.inOdoSelectedMinMax[0] = parseInt(minValue)
    } else {
      state.inOdoSelectedMinMax[0] = state.inOdoMinMax[0]
    }
  },
  setOutOdoSelectedMin (state, minValue) {
    if (!isNaN(parseInt(minValue))) {
      state.outOdoSelectedMinMax[0] = parseInt(minValue)
    } else {
      state.outOdoSelectedMinMax[0] = state.outOdoMinMax[0]
    }
  },
  setInDurSelectedMin (state, minValue) {
    if (!isNaN(parseInt(minValue))) {
      state.inDurSelectedMinMax[0] = parseInt(minValue)
    } else {
      state.inDurSelectedMinMax[0] = state.inDurMinMax[0]
    }
  },
  setOutDurSelectedMin (state, minValue) {
    if (!isNaN(parseInt(minValue))) {
      state.outDurSelectedMinMax[0] = parseInt(minValue)
    } else {
      state.outDurSelectedMinMax[0] = state.outDurMinMax[0]
    }
  },
  setSelectedMaxDuration (state, maxValue) {
    if (!isNaN(parseInt(maxValue))) {
      state.selectedMinMaxDuration[1] = parseInt(maxValue)
    } else {
      state.selectedMinMaxDuration[1] = state.minMaxDuration[1]
    }
  },
  setInOdoSelectedMax (state, maxValue) {
    if (!isNaN(parseInt(maxValue))) {
      state.inOdoSelectedMinMax[1] = parseInt(maxValue)
    } else {
      state.inOdoSelectedMinMax[1] = state.inOdoMinMax[1]
    }
  },
  setOutOdoSelectedMax (state, maxValue) {
    if (!isNaN(parseInt(maxValue))) {
      state.outOdoSelectedMinMax[1] = parseInt(maxValue)
    } else {
      state.outOdoSelectedMinMax[1] = state.outOdoMinMax[1]
    }
  },
  setInDurSelectedMax (state, maxValue) {
    if (!isNaN(parseInt(maxValue))) {
      state.inDurSelectedMinMax[1] = parseInt(maxValue)
    } else {
      state.inDurSelectedMinMax[1] = state.inDurMinMax[1]
    }
  },
  setOutDurSelectedMax (state, maxValue) {
    if (!isNaN(parseInt(maxValue))) {
      state.outDurSelectedMinMax[1] = parseInt(maxValue)
    } else {
      state.outDurSelectedMinMax[1] = state.outDurMinMax[1]
    }
  },
  setSelectedHexagons (state, hexagons) {
    state.selectedHexagons = hexagons
  },
  setLocationAggregates (state, locationAggregates) {
    state.locationAggregates = Object.freeze(locationAggregates)
  },
  resetStateToDefault (state) {
    Object.assign(state, getDefaultState())
  },
  setCountDistributionGraphDataStructure (state) {
    state.graphDataStructure.countDistribution = Object.freeze(state.locationAggregates
      .map(graph => { return { hexagons: graph.hexagons, no_stops: graph.no_stops, no_vehicles: graph.vehicles } })
      .sort(function (a, b) { return a.no_stops - b.no_stops })
      .reverse())
  },
  setCountDistributionLegendGraphDataStructure (state) {
    state.graphDataStructure.countDistributionLegend = Object.freeze(state.locationAggregates
      .map(graph => { return { hexagons: graph.hexagons, no_stops: graph.no_stops } })
      .sort(function (a, b) { return a.no_stops - b.no_stops }))
  },
  setDurationGraphDataStructure (state, plotData) {
    state.graphDataStructure.durationDistribution = Object.freeze(plotData)
  },
  setSelectedMinMaxOdometer (state, minMaxOdometerArray) {
    state.selectedMinMaxOdometer = minMaxOdometerArray
  },
  setSelectedMinOdometer (state, minValue) {
    if (!isNaN(parseInt(minValue))) {
      state.selectedMinMaxOdometer[0] = parseInt(minValue)
    } else {
      state.selectedMinMaxOdometer[0] = state.minMaxOdometer[0]
    }
  },
  setSelectedMaxOdometer (state, maxValue) {
    if (!isNaN(parseInt(maxValue))) {
      state.selectedMinMaxOdometer[1] = parseInt(maxValue)
    } else {
      state.selectedMinMaxOdometer[1] = state.minMaxOdometer[1]
    }
  },
  setLocationDataWithGeoInfo (state) {
    state.locationDataWithGeoInfo = Object.freeze(state.locationAggregates.map(graph => {
      let latLong = h3ToGeo(graph.hexagons)
      let hexResolution = state.hexagonResolution
      return { ...graph, latitude: latLong[0], longitude: latLong[1], resolution: hexResolution, avgAreaKm2: hexArea(hexResolution, 'km2'), avgAreaM2: hexArea(hexResolution, 'm2') }
    })
      .sort(function (a, b) { return a.no_stops - b.no_stops })
      .reverse())
  }
}

const actions = {
  resetState ({ commit }) {
    commit('resetStateToDefault')
  },
  setLocationLayerAsync (context, layer) {
    context.commit('setLocationAggregates', layer)
  },
  setCountDistributionGraphAsync (context) {
    context.commit('setCountDistributionGraphDataStructure')
    context.commit('setCountDistributionLegendGraphDataStructure')
  },
  setLocationDataWithGeoInfoAsync (context) {
    context.commit('setLocationDataWithGeoInfo')
  },
  setEdgeLayerAsync (context, layer) {
    context.commit('setEdgeLayer', layer)
  }
}

export default {
  namespaced: true,
  state: state,
  getters: getters,
  actions: actions,
  mutations: mutations
}
